/**
 * Bridges the app's zustand stores to the assistant's pure snapshot code.
 *
 * The assistant reuses data the app already fetches (no new endpoints). If
 * the landlord opens the assistant before visiting, say, Invoices, the
 * relevant store is still empty, so ensureAssistantData() fetches whatever
 * is missing once per session, in parallel, and never blocks for longer
 * than LOAD_TIMEOUT_MS: a slow endpoint means a slightly thinner answer,
 * not a hung chat.
 */
import { usePlotStore } from '../../stores/plotStore';
import { useContractStore } from '../../stores/contractStore';
import { useInvoiceStore } from '../../stores/invoiceStore';
import usePaymentStore from '../../stores/paymentStore';
import { useLeadStore } from '../../stores/leadStore';
import { useProposalStore } from '../../stores/proposalStore';
import { useActivityStore } from '../../stores/activityStore';
import { useStatusStore } from '../../stores/statusStore';
import useContactStore from '../../stores/contactStore';
import type { AssistantSourceData, ContractLike, PlotLike } from './types';

const LOAD_TIMEOUT_MS = 8000;

interface Loader {
  isEmpty: () => boolean;
  load: () => Promise<unknown>;
}

const LOADERS: Record<string, Loader> = {
  statuses: { isEmpty: () => useStatusStore.getState().statuses.length === 0, load: () => useStatusStore.getState().fetchStatuses() },
  plots: { isEmpty: () => usePlotStore.getState().plots.length === 0, load: () => usePlotStore.getState().fetchPlots() },
  contracts: { isEmpty: () => useContractStore.getState().contracts.length === 0, load: () => useContractStore.getState().fetchContracts() },
  invoices: { isEmpty: () => useInvoiceStore.getState().invoices.length === 0, load: () => useInvoiceStore.getState().fetchInvoices() },
  payments: { isEmpty: () => usePaymentStore.getState().payments.length === 0, load: () => usePaymentStore.getState().fetchPayments() },
  leads: { isEmpty: () => useLeadStore.getState().leads.length === 0, load: () => useLeadStore.getState().fetchLeads() },
  proposals: { isEmpty: () => useProposalStore.getState().proposals.length === 0, load: () => useProposalStore.getState().fetchProposals() },
  activities: { isEmpty: () => useActivityStore.getState().activities.length === 0, load: () => useActivityStore.getState().fetchActivities() },
  contacts: { isEmpty: () => useContactStore.getState().contacts.length === 0, load: () => useContactStore.getState().fetchContacts() },
};

const attempted = new Set<string>();
let inFlight: Promise<void> | null = null;

/** Fetches every still-empty store once per session; resolves within the timeout. */
export function ensureAssistantData(): Promise<void> {
  if (inFlight) return inFlight;
  const pending = Object.entries(LOADERS)
    .filter(([key, loader]) => !attempted.has(key) && loader.isEmpty())
    .map(([key, loader]) => {
      attempted.add(key);
      return loader.load().catch((error: unknown) => {
        console.warn(`Assistant could not load ${key}:`, error);
      });
    });
  if (pending.length === 0) return Promise.resolve();
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, LOAD_TIMEOUT_MS));
  inFlight = Promise.race([Promise.allSettled(pending).then(() => undefined), timeout]).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/** Test hook: forget which stores were already fetched. */
export function resetAssistantDataLoader(): void {
  attempted.clear();
  inFlight = null;
}

function contractsFrom(plots: PlotLike[], contracts: ContractLike[]): ContractLike[] {
  if (contracts.length > 0) return contracts;
  // The contracts store is empty but plots embed their leases: use those.
  const seen = new Set<number>();
  return plots.flatMap((p) => p.contracts ?? []).filter((c) => (seen.has(c.id) ? false : (seen.add(c.id), true)));
}

/** Reads the current store contents in the shape snapshot.ts expects. */
export function readAssistantData(): AssistantSourceData {
  const plots = usePlotStore.getState().plots as unknown as PlotLike[];
  const contracts = useContractStore.getState().contracts as unknown as ContractLike[];
  return {
    statuses: useStatusStore.getState().statuses,
    plots,
    contracts: contractsFrom(plots, contracts),
    invoices: useInvoiceStore.getState().invoices,
    payments: usePaymentStore.getState().payments,
    leads: useLeadStore.getState().leads,
    proposals: useProposalStore.getState().proposals,
    activities: useActivityStore.getState().activities,
    contacts: useContactStore.getState().contacts.map((c) => ({ id: c.id, first_name: c.first_name, last_name: c.last_name })),
  };
}
